import React, { useEffect, useState } from 'react'
import { ReceiptIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { jobMeetsAllPhotoRequirements } from '../../lib/jobPhotoPolicy'
import { useAppSettings } from '../../hooks/useAppSettings'
import { usePermissions } from '../../hooks/usePermissions'
import {
  completeJobField,
  fetchFieldJobLineItems,
  type FieldLineItem,
} from '../../api/fieldJobAccess'
import { isDsnyEveningJob } from '../../lib/routeOptimizer'
import { parseClearAndBlackBags } from '../../lib/garbageBags'
import { snapshotGarbageBagsCost } from '../../api/garbageBagCosts'
import { JobExpenseQuickForm } from './JobExpenseQuickForm'

interface ApartmentUnit {
  id: string
  unit_number_snapshot: string
  tier_description_snapshot: string
  job_line_item_id: string
  outcome: 'completed' | 'carry_forward'
}

interface Props {
  jobId: string
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
}

/** Price-free completion for field roles (no rates / totals shown). */
export function FieldJobCompletionModal({ jobId, isOpen, onClose, onSaved }: Props) {
  const { t } = useTranslation(['jobs', 'common'])
  const { photoPolicy } = useAppSettings()
  const { can } = usePermissions()
  const canSubmitExpenses = can('expenses.submit')
  const [lines, setLines] = useState<FieldLineItem[]>([])
  const [apartments, setApartments] = useState<ApartmentUnit[]>([])
  const [saving, setSaving] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [isDsnyStop, setIsDsnyStop] = useState(false)
  const [clearBags, setClearBags] = useState('')
  const [blackBags, setBlackBags] = useState('')
  const [bagError, setBagError] = useState<string | null>(null)
  const [showExpenses, setShowExpenses] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    ;(async () => {
      const fieldLines = await fetchFieldJobLineItems(jobId)
      setLines(
        fieldLines.map((l) => ({
          ...l,
          actual_quantity: l.actual_quantity ?? l.quantity ?? 1,
        }))
      )
      const [{ data: units }, { data: job }] = await Promise.all([
        supabase
          .from('job_line_item_units')
          .select('id, unit_number_snapshot, tier_description_snapshot, job_line_item_id')
          .eq('job_id', jobId)
          .order('created_at'),
        supabase
          .from('jobs')
          .select(
            'title, service_type, dsny_integration, dsny_pickup_days, garbage_bags_clear, garbage_bags_black'
          )
          .eq('id', jobId)
          .single(),
      ])
      setApartments(
        (units || []).map((u) => ({
          ...u,
          outcome: 'completed' as const,
        }))
      )
      const dsny = isDsnyEveningJob({
        title: job?.title,
        serviceType: job?.service_type,
        dsnyIntegration: job?.dsny_integration,
        dsnyPickupDays: job?.dsny_pickup_days,
      })
      setIsDsnyStop(dsny)
      setClearBags(
        job?.garbage_bags_clear != null && Number(job.garbage_bags_clear) >= 0
          ? String(job.garbage_bags_clear)
          : ''
      )
      setBlackBags(
        job?.garbage_bags_black != null && Number(job.garbage_bags_black) >= 0
          ? String(job.garbage_bags_black)
          : ''
      )
      setBagError(null)
      setShowExpenses(false)
      setPhotoError(null)
    })().catch(() => undefined)
  }, [isOpen, jobId])

  if (!isOpen) return null

  const handleSave = async () => {
    const photoCheck = await jobMeetsAllPhotoRequirements(jobId, photoPolicy)
    if (!photoCheck.ok) {
      setPhotoError(photoCheck.message || t('jobs:photoRequired'))
      return
    }
    let bagsToWrite: { clear: number; black: number } | null = null
    if (isDsnyStop) {
      const bags = parseClearAndBlackBags(clearBags, blackBags)
      if (!bags.ok) {
        setBagError(bags.message)
        return
      }
      bagsToWrite = { clear: bags.clear, black: bags.black }
      setBagError(null)
    }
    setSaving(true)
    try {
      if (bagsToWrite != null) {
        const { error } = await supabase
          .from('jobs')
          .update({
            garbage_bags_clear: bagsToWrite.clear,
            garbage_bags_black: bagsToWrite.black,
            garbage_bags_cost: await snapshotGarbageBagsCost(
              bagsToWrite.clear,
              bagsToWrite.black
            ),
          })
          .eq('id', jobId)
        if (error) throw error
      }
      await completeJobField(jobId, {
        completedUnitIds: apartments
          .filter((a) => a.outcome === 'completed')
          .map((a) => a.id),
        carryForwardUnitIds: apartments
          .filter((a) => a.outcome === 'carry_forward')
          .map((a) => a.id),
        quantityUpdates: lines
          .filter((l) => l.is_variable)
          .map((l) => ({
            line_item_id: l.id,
            actual_quantity: Number(l.actual_quantity || 0),
          })),
      })
      onSaved()
      onClose()
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t('jobs:completion.title')}</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-500">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600">{t('jobs:completion.subtitle')}</p>
          {apartments.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900">{t('jobs:completion.apartments')}</h3>
              {apartments.map((unit) => (
                <div
                  key={unit.id}
                  className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg p-3"
                >
                  <div>
                    <p className="text-sm font-medium">Apt {unit.unit_number_snapshot}</p>
                    <p className="text-xs text-gray-500">{unit.tier_description_snapshot}</p>
                  </div>
                  <select
                    value={unit.outcome}
                    onChange={(e) =>
                      setApartments((rows) =>
                        rows.map((r) =>
                          r.id === unit.id
                            ? { ...r, outcome: e.target.value as ApartmentUnit['outcome'] }
                            : r
                        )
                      )
                    }
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="completed">{t('jobs:completion.completed')}</option>
                    <option value="carry_forward">{t('jobs:completion.carryForward')}</option>
                  </select>
                </div>
              ))}
            </div>
          )}
          {lines.filter((l) => l.is_variable).length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900">Quantities</h3>
              {lines
                .filter((l) => l.is_variable)
                .map((line) => (
                  <label key={line.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-gray-800">{line.description}</span>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={line.actual_quantity ?? ''}
                      onChange={(e) =>
                        setLines((rows) =>
                          rows.map((r) =>
                            r.id === line.id
                              ? { ...r, actual_quantity: Number(e.target.value) }
                              : r
                          )
                        )
                      }
                      className="w-24 px-2 py-1 border border-gray-300 rounded"
                    />
                  </label>
                ))}
            </div>
          )}
          {isDsnyStop && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{t('jobs:completion.clearBags')} / {t('jobs:completion.blackBags')}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t('jobs:completion.bagsRequired')}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('jobs:completion.blackBags')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={blackBags}
                    onChange={(e) => {
                      setBlackBags(e.target.value)
                      setBagError(null)
                    }}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('jobs:completion.clearBags')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={clearBags}
                    onChange={(e) => {
                      setClearBags(e.target.value)
                      setBagError(null)
                    }}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="0"
                  />
                </div>
              </div>
              {bagError && <p className="text-sm text-red-700">{bagError}</p>}
            </div>
          )}
          {showExpenses && canSubmitExpenses && (
            <JobExpenseQuickForm jobId={jobId} onCancel={() => setShowExpenses(false)} />
          )}
          {photoError && <p className="text-sm text-amber-700">{photoError}</p>}
        </div>
        <div className="p-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          >
            {t('common:cancel')}
          </button>
          {canSubmitExpenses && (
            <button
              type="button"
              onClick={() => setShowExpenses((v) => !v)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm inline-flex items-center justify-center gap-1.5"
            >
              <ReceiptIcon className="w-4 h-4" />
              {showExpenses ? t('jobs:completion.hideExpenses') : t('jobs:completion.enterExpenses')}
            </button>
          )}
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {saving ? t('jobs:completion.saving') : t('jobs:completion.save')}
          </button>
        </div>
      </div>
    </div>
  )
}
